if (!customElements.get("sub-collection-carousel")) {
  class SubCollectionCarousel extends HTMLElement {
    constructor() {
      super();
      this.measure = this.measure.bind(this);
      this.scheduleMeasure = this.scheduleMeasure.bind(this);
      this.onResize = this.onResize.bind(this);
      this.rafId = null;
      this.currentOverflow = null;
      this.swiper = null;
      this.isReady = false;
    }

    connectedCallback() {
      if (this.isReady) {
        this.scheduleMeasure();
        return;
      }

      this.isReady = true;
      this.container = this.querySelector(".container");
      this.slider = this.querySelector(".js-sub-collections");
      this.prevButton = this.querySelector('[class*="swiper-button--prev-"]');
      this.nextButton = this.querySelector('[class*="swiper-button--next-"]');

      if (!this.container || !this.slider) {
        return;
      }

      this.resizeObserver = new ResizeObserver(this.scheduleMeasure);
      this.resizeObserver.observe(this.container);

      window.addEventListener("resize", this.onResize);
      window.addEventListener("load", this.onResize);

      if (document.fonts?.ready) {
        document.fonts.ready.then(this.scheduleMeasure);
      }

      this.scheduleMeasure();
    }

    disconnectedCallback() {
      if (this.resizeObserver) {
        this.resizeObserver.disconnect();
      }

      window.removeEventListener("resize", this.onResize);
      window.removeEventListener("load", this.onResize);

      if (this.rafId) {
        cancelAnimationFrame(this.rafId);
        this.rafId = null;
      }

      this.destroySwiper();
    }

    onResize() {
      this.scheduleMeasure();
    }

    scheduleMeasure() {
      if (this.rafId) {
        return;
      }

      this.rafId = requestAnimationFrame(() => {
        this.rafId = null;
        this.measure();
      });
    }

    parseOptions() {
      try {
        return JSON.parse(this.slider?.getAttribute("data-swiper-options") || "{}") || {};
      } catch (_error) {
        return {};
      }
    }

    getSlideWidths() {
      return Array.from(this.slider.querySelectorAll(".swiper-slide")).reduce((totalWidth, slide) => {
        return totalWidth + Math.ceil(slide.getBoundingClientRect().width);
      }, 0);
    }

    getContentWidth() {
      const slides = this.slider.querySelectorAll(".swiper-slide");
      const slideCount = slides.length;
      const options = this.parseOptions();
      const spaceBetween = Number(options.spaceBetweenMobile || 0);
      return this.getSlideWidths() + Math.max(0, slideCount - 1) * spaceBetween;
    }

    getAvailableWidth() {
      return Math.ceil(this.container.clientWidth);
    }

    destroySwiper() {
      if (!this.swiper) return;
      this.swiper.destroy(true, true);
      this.swiper = null;
    }

    buildSwiperOptions(overflow) {
      const options = this.parseOptions();
      const spaceBetweenMobile = Number(options.spaceBetweenMobile || 0);
      const spaceBetweenTablet = Number(options.spaceBetweenTablet ?? options.spaceBetweenDesktop ?? spaceBetweenMobile);
      const spaceBetweenDesktop = Number(options.spaceBetweenDesktop ?? spaceBetweenMobile);
      const slideCount = this.slider.querySelectorAll(".swiper-slide").length;

      return {
        slidesPerView: "auto",
        spaceBetween: spaceBetweenMobile,
        watchOverflow: false,
        centeredSlides: !overflow,
        centeredSlidesBounds: !overflow,
        centerInsufficientSlides: !overflow,
        freeMode: slideCount > 1
          ? {
              enabled: true,
              momentum: true,
              momentumBounce: true,
              sticky: false,
              momentumRatio: 0.85
            }
          : false,
        grabCursor: slideCount > 1,
        simulateTouch: slideCount > 1,
        allowTouchMove: slideCount > 1,
        touchStartPreventDefault: false,
        preventClicks: true,
        preventClicksPropagation: true,
        breakpoints: {
          750: {
            spaceBetween: spaceBetweenTablet
          },
          990: {
            spaceBetween: spaceBetweenDesktop
          }
        },
        navigation: this.prevButton && this.nextButton
          ? {
              prevEl: this.prevButton,
              nextEl: this.nextButton
            }
          : undefined
      };
    }

    initSwiper(overflow) {
      if (!window.Swiper) {
        return;
      }

      const existingSwiper = this.slider.swiper || this.swiper;
      if (existingSwiper) {
        existingSwiper.destroy(true, true);
      }

      this.swiper = new Swiper(this.slider, this.buildSwiperOptions(overflow));
    }

    measure() {
      if (!this.container || !this.slider) {
        return;
      }

      const slides = this.slider.querySelectorAll(".swiper-slide");
      if (!slides.length) {
        return;
      }

      const overflow = this.getContentWidth() > this.getAvailableWidth() + 1;

      if (this.currentOverflow !== overflow) {
        this.currentOverflow = overflow;
        this.dataset.overflow = overflow ? "true" : "false";
        this.classList.toggle("is-overflowing", overflow);
        this.classList.toggle("is-not-overflowing", !overflow);
        this.initSwiper(overflow);
        return;
      }

      if (!this.swiper) {
        this.dataset.overflow = overflow ? "true" : "false";
        this.initSwiper(overflow);
        return;
      }

      this.swiper.update();
    }
  }

  customElements.define("sub-collection-carousel", SubCollectionCarousel);
}
